import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { SUBDOMAIN } from '../lib/subdomains.js';

const ChatContext = createContext(null);

export function useChat() {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChat must be used inside ChatProvider');
  return ctx;
}

function getPageContext(pathname) {
  if (SUBDOMAIN === 'teldire') {
    if (pathname === '/') return 'Campaign landing page for the Teldire campaign. Warm, reading-first archive of session notes and wiki entries.';
    if (pathname.startsWith('/wiki/')) return 'Wiki page for the Teldire campaign — lore articles about people, places, and unresolved campaign mysteries.';
    if (pathname.startsWith('/sessions')) return 'Session notes for the Teldire campaign — recaps with related wiki references and campaign breadcrumbs.';
  }

  if (pathname === '/') return 'Home page of daulton.co — a personal hub with links to projects including the Wexford Reach D&D campaign companion, the Teldire campaign notes, the Get Tilted marble racing game, and an About Me section.';
  if (pathname === '/resume') return 'Resume and portfolio page for Daulton Jenkins — a software developer and former Technical Team Manager based in Fredericton, NB. Shows skills, work experience, and projects.';
  if (pathname === '/teldire') return 'Campaign landing page for the Teldire campaign. Warm, reading-first archive of session notes and wiki entries.';
  if (pathname.startsWith('/teldire/wiki/')) return 'Wiki page for the Teldire campaign — lore articles about people, places, and unresolved campaign mysteries.';
  if (pathname.startsWith('/teldire/sessions')) return 'Session notes for the Teldire campaign — recaps with related wiki references and campaign breadcrumbs.';
  if (pathname.startsWith('/timeline')) return 'Timeline page for the Wexford Reach D&D campaign — a chronological record of in-world events set in the Northern Lugsaas Chain of the Forgotten Realms.';
  if (pathname.startsWith('/wiki')) return 'Placeholder wiki route for the Wexford Reach archive. Public lore pages are not published yet.';
  if (pathname.startsWith('/sessions')) return 'Placeholder sessions route for the Wexford Reach archive. Public session recaps are not published yet.';
  if (pathname.startsWith('/party')) return 'Placeholder party route for the Wexford Reach archive. Public character profiles are not published yet.';
  if (pathname.startsWith('/house-rules')) return 'Public placeholder page for Wexford Reach house rules. The published player-facing ruleset is not available yet.';
  return 'Personal site for Daulton Jenkins at daulton.co.';
}

function loadMessages() {
  try {
    const raw = localStorage.getItem('ask-claude-messages');
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function loadOpen() {
  return localStorage.getItem('ask-claude-open') === 'true';
}

function loadSessionId() {
  const stored = localStorage.getItem('ask-claude-session-id');
  if (stored) return stored;
  const id = crypto.randomUUID();
  localStorage.setItem('ask-claude-session-id', id);
  return id;
}

export function ChatProvider({ children }) {
  const [messages, setMessages] = useState(loadMessages);
  const [isExpanded, setIsExpanded] = useState(loadOpen);
  const [pillMode, setPillMode] = useState('idle');
  const [draft, setDraft] = useState('');
  const [followUp, setFollowUp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [panelPos, setPanelPos] = useState(null);
  const [sessionId, setSessionId] = useState(loadSessionId);

  const location = useLocation();
  const loadingRef = useRef(loading);
  useEffect(() => { loadingRef.current = loading; }, [loading]);

  // Only persist once streaming is complete to avoid many writes mid-stream
  useEffect(() => {
    if (loading) return;
    try {
      localStorage.setItem('ask-claude-messages', JSON.stringify(messages));
    } catch {
      // ignore localStorage errors
    }
  }, [messages, loading]);

  useEffect(() => {
    try {
      localStorage.setItem('ask-claude-open', isExpanded ? 'true' : 'false');
    } catch {
      // ignore localStorage errors
    }
  }, [isExpanded]);

  function openChat() {
    setIsExpanded(true);
  }

  function minimizeChat() {
    setIsExpanded(false);
  }

  function clearConversation() {
    setMessages([]);
    setIsExpanded(false);
    setFollowUp('');
    setDraft('');
    setError('');
    const newId = crypto.randomUUID();
    setSessionId(newId);
    try {
      localStorage.removeItem('ask-claude-messages');
      localStorage.removeItem('ask-claude-open');
      localStorage.setItem('ask-claude-session-id', newId);
    } catch {
      // ignore localStorage errors
    }
  }

  async function sendMessage(text) {
    const trimmed = text.trim().slice(0, 500);
    if (!trimmed || loadingRef.current) return;

    const pageContext = getPageContext(location.pathname).slice(0, 500);
    const timestamp = new Date().toISOString();
    setMessages((prev) => [...prev, { role: 'user', content: trimmed, timestamp }]);
    setLoading(true);
    setError('');
    setPillMode('idle');
    setIsExpanded(true);
    setDraft('');
    setFollowUp('');

    if (import.meta.env.DEV) {
      // Simulate streaming token-by-token in dev mode
      const mockText = '(Dev mode) This is a simulated Claude response. In production, this will be answered by Claude.';
      setMessages((prev) => [...prev, { role: 'assistant', content: '', timestamp: new Date().toISOString() }]);
      let i = 0;
      const interval = setInterval(() => {
        i = Math.min(i + 4, mockText.length);
        setMessages((prev) => {
          const updated = [...prev];
          updated[updated.length - 1] = { ...updated[updated.length - 1], content: mockText.slice(0, i) };
          return updated;
        });
        if (i >= mockText.length) {
          clearInterval(interval);
          setLoading(false);
        }
      }, 30);
      return;
    }

    const history = messages.map(({ role, content }) => ({ role, content }));

    try {
      const res = await fetch('/api/ask', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question: trimmed,
          pageContext,
          pagePath: location.pathname,
          sessionId,
          history,
        }),
      });

      // Non-2xx responses are returned as JSON (e.g. 400, 429, 500)
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || 'Request failed. Please try again.');
        setLoading(false);
        return;
      }

      // Add placeholder assistant message — content grows as deltas arrive
      setMessages((prev) => [...prev, { role: 'assistant', content: '', timestamp: new Date().toISOString() }]);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        // Accumulate all text deltas from this network chunk before updating state
        let chunkText = '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          let event;
          try { event = JSON.parse(line.slice(6)); } catch { continue; }

          if (event.type === 'delta') {
            chunkText += event.text;
          } else if (event.type === 'done') {
            setLoading(false);
          } else if (event.type === 'error') {
            setMessages((prev) => {
              const last = prev[prev.length - 1];
              return last?.role === 'assistant' && !last.content ? prev.slice(0, -1) : prev;
            });
            setError(event.message || 'An error occurred.');
            setLoading(false);
          }
        }

        if (chunkText) {
          setMessages((prev) => {
            const updated = [...prev];
            updated[updated.length - 1] = {
              ...updated[updated.length - 1],
              content: updated[updated.length - 1].content + chunkText,
            };
            return updated;
          });
        }
      }

      // Ensure loading is cleared and drop an empty placeholder (shouldn't happen normally)
      setLoading(false);
      setMessages((prev) => {
        const last = prev[prev.length - 1];
        return last?.role === 'assistant' && !last.content ? prev.slice(0, -1) : prev;
      });

    } catch {
      setMessages((prev) => {
        const last = prev[prev.length - 1];
        return last?.role === 'assistant' && !last.content ? prev.slice(0, -1) : prev;
      });
      setError('Network error — please try again.');
      setLoading(false);
    }
  }

  return (
    <ChatContext.Provider
      value={{
        messages,
        isExpanded,
        pillMode,
        setPillMode,
        draft,
        setDraft,
        followUp,
        setFollowUp,
        loading,
        error,
        panelPos,
        setPanelPos,
        openChat,
        minimizeChat,
        clearConversation,
        sendMessage,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
}
